import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";

const StyledSelectContainer = styled("div")`
  width: 100%;
`;

const StyledSelectField = styled("div")`
  width: 100%;
  position: relative;
`;

const StyledSelectLabel = styled("label")`
  display: inline-block;
  width: max-content;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.palette.common.black};
  font-size: ${({ theme }) => theme.typography.bodyM?.fontSize};
  line-height: ${({ theme }) => theme.typography.bodyM?.lineHeight};
  margin-bottom: 5px;
  font-weight: 300;
  user-select: none;

  & > .select__marker {
    display: inline-block;
    margin-left: 3px;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const StyledSelectTitle = styled(Typography)`
  display: inline-block;
  width: max-content;
  max-width: 100%;
  overflow: hidden;
  font-weight: 300;
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledSelect = styled("button")`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 12px 60px 12px 24px;
  box-sizing: border-box;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.customGrey.soft};
  border: none;
  outline: none;
  transition: all 0.2s linear;
  cursor: pointer;

  &:not(&.select_disabled):hover {
    @media (min-width: 1024px) {
      background-color: ${({ theme }) => theme.palette.common.white};
      box-shadow: 6px 12px 24px 0px rgba(156, 168, 191, 0.24);
      transition: all 0.2s linear;

      & .select__arrow svg {
        stroke: ${({ theme }) => theme.palette.blue.main};
      }
    }
  }

  &.select_open {
    background-color: ${({ theme }) => theme.palette.common.white};
    box-shadow: 6px 12px 24px 0px rgba(156, 168, 191, 0.24);
    transition: all 0.2s linear;

    & .select__arrow svg {
      stroke: ${({ theme }) => theme.palette.blue.main};
    }
  }

  &.select_open {
    & .select__arrow svg {
      transform: rotate(180deg);
    }

    & ~ .select__content {
      opacity: 1;
      visibility: visible;
      transition: opacity 0.1s ease;
    }
  }

  &.select_white,
  &.select_grey {
    border: 1px solid transparent;

    &:not(&.select_error):hover {
      @media (min-width: 1024px) {
        border-color: ${({ theme }) => theme.palette.blue.main};
        box-shadow: none;
      }
    }

    &:not(&.select_error).select_open {
      border-color: ${({ theme }) => theme.palette.blue.main};
      box-shadow: none;
    }
  }

  &.select_white {
    background-color: ${({ theme }) => theme.palette.common.white};

    &:hover {
      @media (min-width: 1024px) {
        background-color: ${({ theme }) =>
          theme.palette.common.white} !important;
      }
    }
  }

  &.select_grey {
    background-color: ${({ theme }) => theme.palette.customGrey.soft};

    & ~ .select__content > .select__list {
      background-color: ${({ theme }) => theme.palette.customGrey.soft};
    }

    &:hover {
      @media (min-width: 1024px) {
        background-color: ${({ theme }) =>
          theme.palette.customGrey.soft} !important;
      }
    }
  }

  &.select_error {
    background-color: ${({ theme }) => theme.palette.red.light};
    border: ${({ theme }) => `1px solid ${theme.palette.red.main}`};

    &:hover {
      @media (min-width: 1024px) {
        background-color: ${({ theme }) => theme.palette.red.light} !important;
        box-shadow: none !important;
      }
    }
  }

  &.select_disabled {
    background-color: ${({ theme }) => theme.palette.customGrey.soft};
    cursor: not-allowed;
    border: none;

    & > .select__arrow svg {
      stroke: ${({ theme }) => theme.palette.customGrey.main};
    }

    &:hover {
      @media (min-width: 1024px) {
        background-color: ${({ theme }) =>
          theme.palette.customGrey.soft} !important;
        box-shadow: none !important;
      }
    }
  }
`;

const StyledSelectArrow = styled("div")`
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  transform: translateY(-50%);
  transition: all 0.2s linear;
  right: 24px;

  & > svg {
    width: 100%;
    transition: all 0.2s linear;
  }
`;

const StyledSelectContent = styled("div")`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 10px;
  bottom: -10px;
  left: 0;
  z-index: 3;
  width: 100%;
  background-color: transparent;
  transform: translateY(100%);
  transition: opacity 0.1s ease;
  opacity: 0;
  visibility: hidden;

  &::-webkit-scrollbar {
    width: 0;
  }
`;

const StyledSelectList = styled("div")`
  width: 100%;
  max-height: 335px;
  overflow: auto;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.common.white};
  box-shadow: 6px 12px 24px 0px rgba(156, 168, 191, 0.24);
`;

const StyledSelectItem = styled("div")<{ grey?: 0 | 1 }>`
  width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  padding: 16px 24px;
  box-sizing: border-box;
  min-height: 56px;
  border-radius: 8px;
  transition: all 0.2s linear;
  background-color: transparent;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.palette.common.black};
  font-size: ${({ theme }) => theme.typography.bodyL?.fontSize};
  line-height: ${({ theme }) => theme.typography.bodyL?.lineHeight};

  &:hover {
    @media (min-width: 1024px) {
      background-color: ${({ theme, grey }) =>
        `${
          grey === 1
            ? theme.palette.customGrey.pure
            : theme.palette.customGrey.soft
        } `};
      transition: all 0.2s linear;

      & .checkbox__custom {
        border-color: ${({ theme }) => theme.palette.green.main};
      }
    }
  }

  &.select__option_active {
    background-color: ${({ theme }) => theme.palette.blue.light};
  }
`;

const StyledSelectError = styled(Typography)`
  display: inline-block;
  opacity: 0;
  visibility: hidden;
  color: ${({ theme }) => theme.palette.red.main};
  transition: all 0.2s linear;
  margin-top: 5px;

  &.select-error_visible {
    opacity: 1;
    visibility: visible;
    transition: all 0.2s linear;
  }
`;

const StyledSelectExtraField = styled("div")<{ grey?: 0 | 1 }>`
  width: 100%;
  border-radius: 8px;
  box-shadow: 6px 2px 24px 0px rgba(156, 168, 191, 0.24);
  background-color: ${({ theme, grey }) =>
    grey === 1 ? theme?.palette.customGrey.soft : theme.palette.common.white};

  & .select-search {
    background-color: inherit;

    & > .MuiInputBase-input,
    & > .MuiInputBase-input::placeholder {
      font-size: ${({ theme }) => theme.typography.bodyM?.fontSize};
      line-height: ${({ theme }) => theme.typography.bodyM?.lineHeight};
    }
  }
`;

const StyledSelectAddAllField = styled("div")`
  width: 100%;
  min-height: 44px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 24px;
  box-sizing: border-box;
`;

const StyledSelectAddNewOptionBtnField = styled("div")`
  width: 100%;
  min-height: 44px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 24px;
  box-sizing: border-box;
`;

const StyledSelectAddNewOptionField = styled("div")`
  width: 100%;
  min-height: 44px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  box-sizing: border-box;

  & .select-new-option {
    background-color: inherit;

    & > .MuiInputBase-input,
    & > .MuiInputBase-input::placeholder {
      font-size: ${({ theme }) => theme.typography.bodyM?.fontSize};
      line-height: ${({ theme }) => theme.typography.bodyM?.lineHeight};
    }
  }
`;

const StyledSelectEmptyData = styled("div")`
  width: 100%;
  min-height: 44px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  box-sizing: border-box;
`;

export {
  StyledSelectContainer,
  StyledSelectLabel,
  StyledSelectField,
  StyledSelectTitle,
  StyledSelect,
  StyledSelectArrow,
  StyledSelectContent,
  StyledSelectList,
  StyledSelectItem,
  StyledSelectError,
  StyledSelectExtraField,
  StyledSelectAddAllField,
  StyledSelectEmptyData,
  StyledSelectAddNewOptionBtnField,
  StyledSelectAddNewOptionField,
};
